import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cx } from "@/lib/cx";

export type PageWidth = "full" | "default" | "narrow";

const widths: Record<PageWidth, string> = {
  full: "",
  default: "max-w-(--content-max)",
  narrow: "max-w-3xl",
};

/**
 * Page header inside the canvas: the page's only h1 (18px / 600), an optional 18px icon, a one-line
 * meta, right-aligned actions, and an optional toolbar row (layout tabs, filter bar) under it.
 * The top bar above only carries the breadcrumb trail.
 */
export function PageHeader({
  title,
  titleSlot,
  titleExtra,
  icon: I,
  meta,
  actions,
  toolbar,
  headingLevel = 1,
  className,
}: {
  title: string;
  /** Replaces the plain heading text, e.g. an inline editor (it then carries the accessible name). */
  titleSlot?: ReactNode;
  /** Next to the heading, e.g. the key. */
  titleExtra?: ReactNode;
  /** 1 on real pages (the page's only h1); 2 only for specimens and nested previews. */
  headingLevel?: 1 | 2;
  icon?: Icon;
  meta?: ReactNode;
  actions?: ReactNode;
  toolbar?: ReactNode;
  className?: string;
}) {
  const H = headingLevel === 1 ? "h1" : "h2";
  return (
    <header className={cx("flex flex-col gap-3 pt-6 pb-5", className)}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <div className="min-w-0 flex-1 basis-60">
          <div className="flex min-w-0 items-center gap-2">
            {I ? <I size={18} aria-hidden="true" className="shrink-0 text-fg-subtle" /> : null}
            <H
              className={cx(
                "text-xl font-semibold tracking-tight text-fg",
                titleSlot ? "min-w-0 shrink leading-7" : "truncate-title",
              )}
              title={titleSlot ? undefined : title}
            >
              {titleSlot ?? title}
            </H>
            {titleExtra}
          </div>
          {meta ? <p className={cx("mt-0.5 text-sm text-fg-muted", I && "pl-[26px]")}>{meta}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {toolbar}
    </header>
  );
}

/**
 * Standard page frame inside the canvas: horizontal padding that matches the top bar, a max width
 * (`full` for lists and boards, `default` 960px for mixed pages, `narrow` for forms), the PageHeader,
 * then the body.
 *
 *   <Page title={m.nav_inbox()} icon={Tray} meta={m.inbox_meta()} actions={<Button ... />}>
 *     <ListState ... />
 *   </Page>
 */
export function Page({
  width = "default",
  children,
  className,
  ...header
}: {
  width?: PageWidth;
  children: ReactNode;
  className?: string;
} & Omit<Parameters<typeof PageHeader>[0], "className">) {
  return (
    <div className={cx("mx-auto w-full px-5 pb-10 sm:px-8", widths[width], className)}>
      <PageHeader {...header} />
      {children}
    </div>
  );
}
